import gsap from "gsap";

export default function initHeader() {
  const submenuBg = document.querySelector(".submenu-bg");
  if (!submenuBg) return;

  const links = document.querySelectorAll(".header__nav__menu__item.parent > a");
  const parents = document.querySelectorAll(".header__nav__menu__item.parent");
  const submenus = document.querySelectorAll(".header__nav__menu__item.parent .submenu");
  const toggle = document.querySelector(".header__toggle");
  const mobileMenu = document.querySelector('.mobile-menu');

  const mm = gsap.matchMedia();

  gsap.set(submenus, { autoAlpha: 0, display: "none" });
  submenus.forEach((sm) => sm.setAttribute("aria-hidden", "true"));

  // Event listener to close search/menu from other scripts (e.g. search.js)
  document.addEventListener('dublin-zoo:close-all-search-ui', () => {
    // We check if functions exist. Note: they might be defined later but will be available via closure
    if (typeof closeSearch === 'function') closeSearch();
    if (toggle && toggle.classList.contains("is-open")) {
      // For mobile menu closure
      const open = toggle.classList.contains("is-open");
      if (open) {
        toggle.click(); // This triggers the onToggle logic
      }
    }
  });

  const hideAllSubmenus = () => {
    submenus.forEach((sm) => {
      gsap.killTweensOf(sm);
      gsap.to(sm, {
        autoAlpha: 0,
        duration: 0.2,
        ease: "power2.inOut",
        onStart: () => {
          // Move focus away before setting aria-hidden — the ARIA spec blocks
          // aria-hidden on any ancestor of the currently focused element.
          if (sm.contains(document.activeElement)) {
            const parentLink = sm
              .closest(".header__nav__menu__item.parent")
              ?.querySelector(":scope > a");
            if (parentLink) parentLink.focus();
            else document.activeElement.blur();
          }
          sm.setAttribute("aria-hidden", "true");
        },
        onComplete: () => (sm.style.display = "none"),
        overwrite: "auto",
      });
    });
    links.forEach((l) => {
      l.setAttribute("aria-expanded", "false");
      l.classList.remove("active");
    });
    parents.forEach((li) => li.classList.remove("active"));
  };

  const getSubmenu = (link) =>
    link?.closest(".header__nav__menu__item.parent")?.querySelector(".submenu");

  const showSubmenu = (submenu) => {
    if (!submenu) return;
    gsap.killTweensOf(submenu);
    submenu.style.display = "flex";
    submenu.setAttribute("aria-hidden", "false");
    gsap.to(submenu, {
      autoAlpha: 1,
      duration: 0.3,
      ease: "power2.out",
      delay: 0.08,
      overwrite: "auto",
    });
  };



  const panel = document.querySelector(".header__tickets");
  const openers = document.querySelectorAll("header .btn--tickets, .tickets-mobile");
  const closer = document.querySelector(".header__tickets__close");

  let ticketsAriaLive = document.querySelector('#tickets-aria-live');
  if (!ticketsAriaLive) {
    ticketsAriaLive = document.createElement('div');
    ticketsAriaLive.id = 'tickets-aria-live';
    ticketsAriaLive.style.cssText = 'position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); border:0;';
    ticketsAriaLive.setAttribute('aria-live', 'polite');
    document.body.appendChild(ticketsAriaLive);
  }

  let isTicketsOpen = false;

  gsap.set(panel, { autoAlpha: 0, scale: 0.9 });

  let ticketsTriggerElement = null;

  const getFocusableElements = (container) => {
    if (!container) return [];
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  };

  const handleTicketsFocusTrap = (e) => {
    if (!isTicketsOpen) return;

    const focusableElements = getFocusableElements(panel);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      closeTickets();
    }
  };

  const openTickets = () => {
    if (isTicketsOpen) return;
    ticketsTriggerElement = document.activeElement;
    isTicketsOpen = true;
    if (ticketsAriaLive) ticketsAriaLive.textContent = "Tickets menu opened";
    openers.forEach((b) => b.setAttribute("aria-expanded", "true"));
    gsap.killTweensOf(panel);
    gsap.to(panel, {
      autoAlpha: 1,
      scale: 1,
      duration: 0.25,
      ease: "back.out(1.7)",
      overwrite: "auto",
      onComplete: () => {
        const focusable = getFocusableElements(panel);
        if (focusable.length > 0) focusable[0].focus();
        document.addEventListener('keydown', handleTicketsFocusTrap);
        panel.setAttribute('aria-hidden', 'false');
      }
    });
  };

  const closeTickets = () => {
    if (!isTicketsOpen) return;
    isTicketsOpen = false;
    if (ticketsAriaLive) ticketsAriaLive.textContent = "Tickets menu closed";
    openers.forEach((b) => b.setAttribute("aria-expanded", "false"));
    document.removeEventListener('keydown', handleTicketsFocusTrap);
    gsap.killTweensOf(panel);
    gsap.to(panel, {
      autoAlpha: 0,
      scale: 0.9,
      duration: 0.2,
      ease: "back.out(1.7)",
      overwrite: "auto",
      onComplete: () => {
        if (ticketsTriggerElement && typeof ticketsTriggerElement.focus === 'function') {
          ticketsTriggerElement.focus();
        }
        ticketsTriggerElement = null;
        panel.setAttribute('aria-hidden', 'true');
      }
    });
  };

  openers.forEach((btn) => {
    btn.setAttribute("aria-haspopup", "true");
    btn.setAttribute("aria-expanded", "false");
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      isTicketsOpen ? closeTickets() : openTickets();
    });
  });

  // Close on link click
  const panelLinks = panel.querySelectorAll("a");
  panelLinks.forEach((link) => {
    link.addEventListener("click", () => {
      closeTickets();
    });
  });

  // Close button
  closer.addEventListener("click", (e) => {
    e.preventDefault();
    closeTickets();
  });

  document.addEventListener("click", (e) => {
    if (!isTicketsOpen) return;
    const clickInsidePanel = panel.contains(e.target);
    const clickOnOpener = e.target.closest(".btn--tickets, .tickets-mobile");
    if (!clickInsidePanel && !clickOnOpener) closeTickets();
  });





  const headerSearchBtn = document.querySelector('.header__search');
  const headerSearch = document.querySelector('.header__content .form');
  const headerNav = document.querySelector('.header__nav');
  const input = headerSearch?.querySelector('.form__search');
  const searchCloseBtn = document.querySelector('.header-search__close');

  // For focus trap and restoration
  let searchTriggerElement = null;
  let isOpenSearch = false;
  let outsideHandlerAttached = false;



  // Focus trap handler
  const handleFocusTrap = (e) => {
    if (!isOpenSearch) return;

    const focusableElements = getFocusableElements(headerSearch);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Trap focus within modal
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }

    // Close on Escape key
    if (e.key === 'Escape') {
      e.preventDefault();
      closeSearch();
    }
  };

  // Base states
  gsap.set(headerSearch, { autoAlpha: 0 });
  gsap.set(headerSearchBtn, { autoAlpha: 1 });
  gsap.set(headerNav, { autoAlpha: 1 });

  const attachOutsideHandler = () => {
    if (outsideHandlerAttached) return;
    setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown, { capture: true });
      outsideHandlerAttached = true;
    }, 0);
  };

  const detachOutsideHandler = () => {
    if (!outsideHandlerAttached) return;
    document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
    outsideHandlerAttached = false;
  };

  const openSearch = () => {
    // Store the element that triggered the search for focus restoration
    searchTriggerElement = document.activeElement;

    gsap.to(headerNav, { autoAlpha: 0, duration: 0.3, overwrite: true });
    gsap.to(headerSearchBtn, { autoAlpha: 0, duration: 0.3, overwrite: true });
    gsap.to(headerSearch, {
      autoAlpha: 1, duration: 0.3, overwrite: true,
      onComplete: () => {
        input?.focus();
        // Add focus trap listener
        document.addEventListener('keydown', handleFocusTrap);
        // Set aria-hidden states
        headerSearch?.setAttribute('aria-hidden', 'false');
        headerNav?.setAttribute('aria-hidden', 'true');
      }
    });

    isOpenSearch = true;
    attachOutsideHandler();
  };

  const closeSearch = () => {
    gsap.to(headerNav, { autoAlpha: 1, duration: 0.3, overwrite: true });
    gsap.to(headerSearchBtn, { autoAlpha: 1, duration: 0.3, overwrite: true });
    gsap.to(headerSearch, {
      autoAlpha: 0, duration: 0.3, overwrite: true,
      onComplete: () => {
        // Restore focus to the element that opened search
        if (searchTriggerElement && typeof searchTriggerElement.focus === 'function') {
          searchTriggerElement.focus();
        }
        searchTriggerElement = null;

        // Set aria-hidden states
        headerSearch?.setAttribute('aria-hidden', 'true');
        headerNav?.setAttribute('aria-hidden', 'false');
      }
    });

    isOpenSearch = false;
    detachOutsideHandler();
    // Remove focus trap listener
    document.removeEventListener('keydown', handleFocusTrap);
  };

  const handlePointerDown = (e) => {
    // Ignore non-primary buttons
    if (e.button !== undefined && e.button !== 0) return;

    const target = e.target;
    // If click is inside search form or the button, don't close
    if (headerSearch.contains(target) || headerSearchBtn.contains(target)) return;

    // Otherwise close
    closeSearch();
  };

  // Toggle on button
  headerSearchBtn?.addEventListener("click", (e) => {
    e.stopPropagation();
    if (!isOpenSearch) openSearch();
    else closeSearch();
  });

  // Close button handler
  searchCloseBtn?.addEventListener("click", (e) => {
    e.stopPropagation();
    closeSearch();
  });




  // =========================
  // DESKTOP (>= 992px)
  // =========================
  mm.add("(min-width: 992px)", () => {
    gsap.set(submenuBg, { yPercent: -71, y: 0 });

    let isOpen = false;
    let currentLink = null;

    const updateBgHeightFor = (submenu) => {
      if (!submenu) return;
      const header = document.querySelector('.header');
      if (!header) return;
      const subRect = submenu.getBoundingClientRect();
      const headRect = header.getBoundingClientRect();
      const targetH = Math.ceil(subRect.bottom - headRect.top) + 100;
      if (!Number.isNaN(targetH) && targetH > 0) {
        submenuBg.style.height = `${targetH}px`;
      }
    };

    const openMenu = (link) => {
      const submenu = getSubmenu(link);
      hideAllSubmenus();
      link.setAttribute("aria-expanded", "true");
      link.classList.add("active");
      link.closest(".header__nav__menu__item.parent")?.classList.add("active");
      // Ensure submenu is visible for measurement
      showSubmenu(submenu);
      // Next frame, measure and animate bg in
      requestAnimationFrame(() => {
        updateBgHeightFor(submenu);
        gsap.to(submenuBg, {
          yPercent: 0,
          duration: 0.6,
          ease: "back.out(1.7)",
          overwrite: "auto",
        });
      });
      isOpen = true;
      currentLink = link || null;
    };

    const closeMenu = () => {
      submenuBg.style.height = '473px';
      gsap.to(submenuBg, {
        yPercent: -71,
        duration: 0.8,
        ease: "power3.inOut",
        overwrite: "auto",
      });
      isOpen = false;
      currentLink = null;
      hideAllSubmenus();
    };

    const onLinkClick = (e) => {
      e.preventDefault();
      const link = e.currentTarget;
      if (isOpen && currentLink === link) {
        closeMenu();
        return;
      }
      openMenu(link);
    };

    const onDocClick = (e) => {
      const isParentLink = e.target.closest(".header__nav__menu__item.parent > a");
      const isInsideSub = e.target.closest(".header__nav__menu__item.parent .submenu");
      if (!isParentLink && !isInsideSub && isOpen) closeMenu();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeMenu();
      }
    };


    links.forEach((l) => l.addEventListener("click", onLinkClick));
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKeyDown);

    // Close submenu when a submenu link is clicked (desktop)
    const submenuLinks = document.querySelectorAll(
      ".header__nav__menu__item.parent .submenu .submenu__menu__item__link, .header__nav__menu__item.parent .submenu .submenu__featured__link"
    );
    const onSubmenuLinkClick = () => {
      if (isOpen) closeMenu();
    };
    submenuLinks.forEach((l) => l.addEventListener("click", onSubmenuLinkClick));

    // Keep background height in sync on resize
    const onResize = () => {
      if (!isOpen || !currentLink) return;
      const submenu = getSubmenu(currentLink);
      if (submenu) updateBgHeightFor(submenu);
    };
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      links.forEach((l) => l.removeEventListener("click", onLinkClick));
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
      submenuLinks.forEach((l) => l.removeEventListener("click", onSubmenuLinkClick));
      window.removeEventListener('resize', onResize);
      // Clear inline height so mobile styles remain unaffected
      if (submenuBg) submenuBg.style.height = '';
      gsap.set(submenuBg, { yPercent: -82 });
      hideAllSubmenus();
    };
  });

  // =========================
  // MOBILE (<= 991px)
  // =========================
  mm.add("(max-width: 991px)", () => {
    gsap.set(submenuBg, { yPercent: -97, y: 0 });

    if (!toggle) return;

    toggle.classList.remove("is-open");
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Open menu");

    let mobileSearchTriggerElement = null;

    const handleMobileMenuFocusTrap = (e) => {
      if (!toggle.classList.contains("is-open")) return;

      const container = document.querySelector('.header');
      if (!container) return;

      const focusableElements = getFocusableElements(container);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        onToggle();
      }
    };

    const openMobile = () => {
      mobileSearchTriggerElement = document.activeElement;
      gsap.to(submenuBg, {
        yPercent: -2,
        duration: 0.4,
        ease: "power3.inOut",
        overwrite: "auto",
        onComplete: () => {
          gsap.to(mobileMenu, {
            autoAlpha: 1,
            duration: 0.2,
            onComplete: () => {
              const focusable = getFocusableElements(document.querySelector('.header'));
              if (focusable.length > 0) focusable[0].focus();
              document.addEventListener('keydown', handleMobileMenuFocusTrap);
            }
          });
        }
      });
    };

    const closeMobile = () => {
      document.removeEventListener('keydown', handleMobileMenuFocusTrap);
      gsap.to(mobileMenu, {
        autoAlpha: 0,
        duration: 0.2,
        onComplete: () => {
          gsap.to(submenuBg, {
            yPercent: -97,
            duration: 0.6,
            ease: "power3.inOut",
            overwrite: "auto",
            onComplete: () => {
              if (mobileSearchTriggerElement) mobileSearchTriggerElement.focus();
            }
          });
        }
      });

    };

    const onToggle = () => {
      const open = toggle.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", String(open));
      toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      open ? openMobile() : closeMobile();
    };

    toggle.addEventListener("click", onToggle);


    const itemsMobile = document.querySelectorAll(".mobile-menu__nav__menu__item.parent");

    itemsMobile.forEach((item) => {
      const trigger = item.querySelector(".mobile-menu__nav__menu__item__link");
      const panel = item.querySelector(".submenu");
      if (!trigger || !panel) return;

      // base state
      gsap.set(panel, { height: 0, autoAlpha: 0, marginBottom: 0 });

      // timeline plays = open, reverse = close
      const tl = gsap.timeline({
        paused: true,
        defaults: { duration: 0.2, ease: "power2.out" },
        onStart: () => panel.style.visibility = "visible",
        onComplete: () => item.classList.add("is-open"),
        onReverseComplete: () => {
          item.classList.remove("is-open");
          panel.style.visibility = "hidden";
        }
      })
        .to(panel, { height: "auto", autoAlpha: 1, marginBottom: 20 });

      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        if (item.classList.contains("is-open")) {
          tl.reverse();
        } else {
          tl.play(0);
        }
      });

    });

    // Close mobile menu when a submenu link is clicked
    const mobileSubmenuLinks = mobileMenu.querySelectorAll(".submenu__item__link");
    mobileSubmenuLinks.forEach((link) => {
      link.addEventListener("click", () => {
        if (toggle.classList.contains("is-open")) {
          onToggle();
        }
      });
    });

    // Close mobile menu when a featured item link is clicked
    const mobileFeaturedLinks = mobileMenu.querySelectorAll(".submenu__featured__link");
    mobileFeaturedLinks.forEach((link) => {
      link.addEventListener("click", () => {
        if (toggle.classList.contains("is-open")) {
          onToggle();
        }
      });
    });

    // Close mobile menu when a top-level link without submenu is clicked
    const mobileTopLevelLinks = mobileMenu.querySelectorAll(".mobile-menu__nav__menu__item:not(.parent) > .mobile-menu__nav__menu__item__link");
    mobileTopLevelLinks.forEach((link) => {
      link.addEventListener("click", () => {
        if (toggle.classList.contains("is-open")) {
          onToggle();
        }
      });
    });
  });

  return () => {
    toggle.removeEventListener("click", onToggle);
    toggle.classList.remove("is-open");
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Open menu");
    gsap.set(submenuBg, { yPercent: -71 });
  };
}

// Global Video Pause/Play Toggle Logic
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.btn-pause-bg-video');
  if (!btn) return;
  const container = btn.parentElement;
  const videos = container.querySelectorAll('video[autoplay]');
  if (!videos.length) return;

  const iconPause = btn.querySelector('.icon-pause');
  const iconPlay = btn.querySelector('.icon-play');

  // Use the first video's state to determine toggle direction
  const isPaused = videos[0].paused;

  if (isPaused) {
    videos.forEach(v => v.play());
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', 'Pause background video');
    if (iconPause) iconPause.style.display = 'block';
    if (iconPlay) iconPlay.style.display = 'none';
  } else {
    videos.forEach(v => v.pause());
    btn.setAttribute('aria-pressed', 'true');
    btn.setAttribute('aria-label', 'Play background video');
    if (iconPause) iconPause.style.display = 'none';
    if (iconPlay) iconPlay.style.display = 'block';
  }
});

// Respect prefers-reduced-motion globally
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
if (prefersReducedMotion.matches) {
  setTimeout(() => {
    document.querySelectorAll('video[autoplay]').forEach(v => {
      v.removeAttribute('autoplay');
      v.pause();
      const btn = v.parentElement.querySelector('.btn-pause-bg-video');
      if (btn) {
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Play background video');
        const iconPause = btn.querySelector('.icon-pause');
        const iconPlay = btn.querySelector('.icon-play');
        if (iconPause) iconPause.style.display = 'none';
        if (iconPlay) iconPlay.style.display = 'block';
      }
    });
  }, 1000);
}
